/* verqo — /buyers · dedicated retailer funnel
   Faire-style split: this is the buyer-side full landing.
   Reuses MoatTile + BUYER_MOAT_TILES from LandingV2Preview, plus the BuyerCreditGraph
   from BuyerHeroV2Preview, so visual language stays consistent with the main site.
*/
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, CheckCircle2, CircleDot, ChevronDown, Plug, FileSignature, Banknote,
} from "lucide-react";
import { VerqoLockup } from "../../components/VerqoLockup";
import { BuyerCreditGraph } from "./BuyerHeroV2Preview";
import { MoatTile, BUYER_MOAT_TILES, INDUSTRIES, BUYER_ROADMAP, TRUST_BADGES } from "./LandingV2Preview";
import { BuyerOffNetworkSection } from "./BuyersOffNetworkPreview";
import MarketingMobileNav from "../../components/MarketingMobileNav";

const Eyebrow = ({ children, testid }) => (
  <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold" data-testid={testid}>
    <CircleDot className="w-2.5 h-2.5 fill-lime-500 text-lime-500" />
    {children}
  </div>
);

const Reveal = ({ children, delay = 0, y = 24 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const BUYER_FAQS = [
  {
    q: "Does it really cost $0 to use verqo as a buyer?",
    a: "The verqo platform is free for buyers — no subscription, no application fees, no underwriting fees, no credit-line fees. Suppliers cover our cost. You're still responsible for the normal pass-through costs of doing business (wire fees, card processing, shipping, and any dropship or fulfillment fees), and we don't mark those up.",
  },
  {
    q: "How fast is the credit decision?",
    a: "Under 60 seconds. You connect your bank via Plaid, we verify your EIN, and our AI underwriter drafts your credit memo and approves your line in real time. No paper trade references. No multi-week waits.",
  },
  {
    q: "What if my suppliers aren't on verqo yet?",
    a: "Two options. (1) You can still get pre-approved and use the proof to negotiate better terms with off-network suppliers. (2) Use our PO Generator to send your supplier a verqo-issued PO with the payment terms you want — they onboard in minutes to fulfill it, and you become their preferred buyer.",
  },
  {
    q: "Can I use my credit line at any supplier on verqo?",
    a: "Yes. Apply once, use it at every supplier on the network. Each seller sets the maximum term they offer (Net-30, 60, or 90), and your line + payment history is honored across all of them. The better your record, the better the terms you unlock.",
  },
  {
    q: "What categories of suppliers are on verqo?",
    a: "Fashion & apparel, beauty, food & beverage, home goods, pet, wellness, luxury goods, and tech accessories. We focus on independent and emerging wholesale brands doing $1M–$100M — the brands your customers actually want.",
  },
  {
    q: "Is my financial data secure?",
    a: "Bank-grade. We use Plaid for read-only bank connections, never store credentials, and are SOC 2 Type II audited. Your data is encrypted in transit and at rest.",
  },
];

export default function BuyersPage({ heroVariant = null, trimmedForPreview = false, hideHeader = false, hideFooter = false, hideNonEssentialSections = false } = {}) {
  // FAQ section moved to the dedicated /faq route (Feb 2026).
  useEffect(() => { document.title = "verqo for retailers · One credit line. Every supplier."; }, []);

  // Hero — rotating headline highlight (Feb 2026, mirrors seller landing).
  // Cycles through the 4 buyer-side value props every 2.5s.
  // The `heroVariant` prop is kept for back-compat with the preview route
  // (`/preview/buyers-hero-v2`) but the rotation is now the default
  // experience on `/buyers` too.
  const BUYER_HERO_ROTATING_WORDS = [
    "on Net-60 in 60 seconds.",
    "approved once, used everywhere.",
    "paid in one click.",
    "fully automated, end-to-end.",
  ];
  const [buyerHeroIdx, setBuyerHeroIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setBuyerHeroIdx((x) => (x + 1) % BUYER_HERO_ROTATING_WORDS.length), 2500);
    return () => clearInterval(t);
  }, []);
  const buyerRotatingWord = BUYER_HERO_ROTATING_WORDS[buyerHeroIdx];

  return (
    <div
      className="min-h-screen text-slate-900 antialiased selection:bg-lime-300 selection:text-slate-900"
      style={{ background: "#FAFAF7" }}
      data-testid="buyers-page"
      data-trimmed-preview={trimmedForPreview ? "true" : undefined}
      data-hide-header={(hideHeader || trimmedForPreview) ? "true" : undefined}
      data-hide-nonessential={(hideNonEssentialSections || trimmedForPreview) ? "true" : undefined}
    >
      {(trimmedForPreview || hideHeader || hideNonEssentialSections) && (
        <style>{`
          /* Feb 2026 · Nav-integration mode */
          [data-hide-nonessential="true"] section[data-testid="buyers-roadmap"] {
            display: none !important;
          }
          [data-hide-header="true"] section[data-testid="buyers-hero"] {
            padding-top: 4rem !important;
          }
          @media (min-width: 768px) {
            [data-hide-header="true"] section[data-testid="buyers-hero"] {
              padding-top: 3rem !important;
            }
          }
        `}</style>
      )}
      {/* Hero rotating word — keyframe (mirrors seller landing). Scoped to
          this page only. */}
      <style>{`
        @keyframes verqo-word-rotate {
          0%   { opacity: 0.05; transform: translateY(8px); }
          100% { opacity: 1;    transform: translateY(0); }
        }
        .verqo-word-rotate { animation: verqo-word-rotate 0.22s ease-out; }
      `}</style>
      {/* Floating nav (mirrors main site)
          Suppressed when the external mega-menu header is used. */}
      {!(trimmedForPreview || hideHeader) && (
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-white/80 border-b border-slate-200/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 rounded-md"
            aria-label="Back to top"
            data-testid="buyers-nav-home"
          >
            <VerqoLockup />
            <span className="ml-1 text-[11px] text-slate-500 hidden lg:inline group-hover:text-slate-900 transition-colors">· Wholesale, instant.</span>
          </button>
          <nav className="hidden lg:flex items-center gap-7 text-[12px] text-slate-600">
            <a href="#paths"        data-testid="buyers-nav-platform" className="hover:text-slate-900 transition-colors">Platform</a>
            <a href="#tools"        data-testid="buyers-nav-how" className="hover:text-slate-900 transition-colors">How it works</a>
            <a href="#bento"        data-testid="buyers-nav-why" className="hover:text-slate-900 transition-colors">Why verqo</a>
            <a href="#pricing"      data-testid="buyers-nav-pricing" className="hover:text-slate-900 transition-colors">Pricing</a>
            <Link to="/dropship?as=buyer" data-testid="buyers-nav-dropship" className="hover:text-slate-900 transition-colors">
              Dropship
            </Link>
            <Link to="/" data-testid="buyers-nav-for-sellers" className="text-slate-500 hover:text-slate-900 transition-colors">For sellers</Link>
          </nav>
          <div className="hidden lg:flex items-center gap-4">
            <a href="/login?as=buyer" data-testid="buyers-nav-signin" className="text-[12px] text-slate-600 hover:text-slate-900 transition-colors">Sign in</a>
            <a href="/waitlist?as=buyer" data-testid="buyers-nav-cta" className="text-[12px] px-4 py-2 rounded-md text-white hover:opacity-90 transition-opacity font-medium bg-[#0c1612]">Get pre-approved</a>
          </div>
          <a href="/waitlist?as=buyer" data-testid="buyers-nav-cta-mobile" className="lg:hidden text-[12px] px-3.5 py-2 rounded-md text-white hover:opacity-90 transition-opacity font-medium bg-[#0c1612]">Pre-approve</a>
          <MarketingMobileNav
            items={[
              { label: "Platform",     href: "#paths" },
              { label: "How it works", href: "#tools" },
              { label: "Why verqo",    href: "#bento" },
              { label: "Pricing",      href: "#pricing" },
              { label: "Dropship", href: "/dropship?as=buyer" },
              { label: "For sellers",  href: "/" },
              { label: "About",        href: "/about" },
              { label: "Partners",     href: "/partners" },
              { label: "FAQ",          href: "/faq" },
              { label: "Contact",      href: "/contact" },
            ]}
            cta={{ label: "Get pre-approved", href: "/waitlist?as=buyer" }}
          />
        </div>
      </header>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* HERO — same as buyer hero V2 on main site                       */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section id="hero" className="relative pt-28 md:pt-36 pb-12 md:pb-16 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="buyers-hero">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          <div>
            <Reveal>
              <Eyebrow testid="buyers-hero-eyebrow">For Retailers, independent shops &amp; Online Resellers · $250K–$100M</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-5 text-[40px] md:text-[60px] lg:text-[68px] leading-[0.96] tracking-tight font-semibold text-slate-900" data-testid="buyers-hero-headline">
                Wholesale,<br />
                <span key={buyerHeroIdx} className="bg-lime-300 px-2 -rotate-[0.4deg] inline-block verqo-word-rotate" data-testid="buyers-hero-rotating-word">
                  {buyerRotatingWord}
                </span>
              </h1>
            </Reveal>
            <Reveal delay={0.22}>
              <p className="mt-7 max-w-xl text-[16px] md:text-[18px] leading-relaxed text-slate-600" data-testid="buyers-hero-subhead">
                Run your entire wholesale buying from one portal — <strong className="text-slate-900">flexible Net-30/60/90 terms, one-click payments, real-time shipping, and automation end-to-end.</strong>
              </p>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a href="/waitlist?as=buyer" data-testid="buyers-hero-cta-primary" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-all bg-[#0c1612]">
                  Get pre-approved — free
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a href="/login?as=buyer" data-testid="buyers-hero-signin" className="text-[13.5px] text-slate-600 hover:text-slate-900 transition-colors">
                  Already on verqo? Sign in →
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.44}>
              <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-slate-500">
                <div className="inline-flex items-center gap-1.5" data-testid="buyers-hero-promise-1"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> Approved in 60 seconds</div>
                <div className="inline-flex items-center gap-1.5" data-testid="buyers-hero-promise-2"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> $0 platform fee — ever</div>
              </div>
            </Reveal>
          </div>

          {/* RIGHT — 1-to-Many credit graphic */}
          <Reveal delay={0.15} y={32}>
            <div className="relative rounded-2xl p-6 md:p-7 overflow-hidden shadow-[0_40px_80px_-32px_rgba(15,23,42,0.45)] bg-[#0c1612]" data-testid="buyers-hero-graphic-card">
              <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "28px 28px" }}
              />
              <div className="absolute inset-0 opacity-30 pointer-events-none"
                style={{ background: "radial-gradient(circle at 50% 40%, rgba(220,255,58,0.18) 0%, transparent 55%)" }}
              />
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.26em] font-semibold text-lime-400">Your portable credit line</div>
                  <div className="inline-flex items-center gap-1.5 text-[10px] text-white/55 uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-lime-300 animate-pulse" /> Live
                  </div>
                </div>
                <BuyerCreditGraph />
                <div className="mt-3 text-center">
                  <div className="text-[19px] md:text-[21px] font-semibold text-white tracking-tight leading-tight">
                    One line. <span className="text-white/45">Every supplier.</span>
                  </div>
                  <div className="text-[11.5px] text-white/55 mt-1.5 max-w-[320px] mx-auto leading-relaxed">
                    Approved once · honored at every brand on verqo · use the proof to negotiate harder off-network.
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* CATEGORIES BAND — mirrors main / page (compact chips strip)     */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section className="relative pb-12 md:pb-16 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="buyers-categories">
        <Reveal>
          <p className="text-center text-[12px] uppercase tracking-[0.22em] text-slate-500 mb-5">
            Suppliers across these categories — already on verqo
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2.5 md:gap-3" data-testid="buyers-industries-strip">
            {INDUSTRIES.map((i) => (
              <div
                key={i.name}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 bg-white hover:border-lime-400 hover:bg-lime-50/40 hover:-translate-y-0.5 transition-all"
                data-testid={`buyers-industry-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <i.icon className="w-3.5 h-3.5 text-slate-600" strokeWidth={1.8} />
                <span className="text-[12.5px] font-medium text-slate-700">{i.name}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* THREE WAYS — high-level router into the right deep-dive section  */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section id="paths" className="relative py-10 md:py-14 px-6 md:px-12 max-w-[1280px] mx-auto bg-slate-50/40" data-testid="buyers-three-ways">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <Eyebrow>Three ways to win on verqo</Eyebrow>
            <h2 className="mt-5 text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 leading-snug">
              However you wholesale — verqo fits.
            </h2>
            <p className="mt-3 text-[13.5px] md:text-[14.5px] text-slate-600 leading-relaxed max-w-xl mx-auto">
              Whether your suppliers are on the network or not, there's a way for you to get terms, get financed, and run smoother — starting today.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Card 1 — On-network buying with credit line */}
          <Reveal delay={0.05}>
            <a href="#bento" className="group h-full block rounded-2xl border border-slate-200 bg-white p-6 md:p-7 hover:border-lime-300 hover:-translate-y-0.5 transition-all" data-testid="buyers-way-credit-line">
              <div className="flex items-center justify-between mb-5">
                <div className="w-10 h-10 rounded-lg bg-lime-100 grid place-items-center">
                  <Plug className="w-4 h-4 text-lime-700" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Way 1</div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 leading-snug">
                Buy on credit at <span className="text-slate-400">every supplier on verqo.</span>
              </h3>
              <p className="mt-3 text-[13px] leading-relaxed text-slate-600">
                Sign up, get pre-approved in 60 seconds, and place POs with <strong className="text-slate-900">Net-30/60/90 terms</strong> baked in. One credit line, every brand on the network.
              </p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-900 group-hover:text-lime-700 transition-colors">
                See what you get
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </a>
          </Reveal>

          {/* Card 2 — Off-network PO hub */}
          <Reveal delay={0.12}>
            <a href="#tools" className="group h-full block rounded-2xl border border-slate-200 bg-white p-6 md:p-7 hover:border-lime-300 hover:-translate-y-0.5 transition-all" data-testid="buyers-way-po-hub">
              <div className="flex items-center justify-between mb-5">
                <div className="w-10 h-10 rounded-lg bg-lime-100 grid place-items-center">
                  <FileSignature className="w-4 h-4 text-lime-700" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Way 2</div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 leading-snug">
                Run off-network suppliers <span className="text-slate-400">through your verqo hub.</span>
              </h3>
              <p className="mt-3 text-[13px] leading-relaxed text-slate-600">
                Send POs with your terms baked in to <strong className="text-slate-900">any supplier</strong> — even off-network. Acceptances, invoices, and tracking thread back into one inbox.
              </p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-900 group-hover:text-lime-700 transition-colors">
                See the PO &amp; Inbox tools
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </a>
          </Reveal>

          {/* Card 3 — Invoice financing */}
          <Reveal delay={0.19}>
            <a href="#tools" className="group h-full block rounded-2xl border border-slate-200 bg-white p-6 md:p-7 hover:border-lime-300 hover:-translate-y-0.5 transition-all" data-testid="buyers-way-invoice-financing">
              <div className="flex items-center justify-between mb-5">
                <div className="w-10 h-10 rounded-lg bg-lime-100 grid place-items-center">
                  <Banknote className="w-4 h-4 text-lime-700" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Way 3</div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 leading-snug">
                Finance any invoice — <span className="text-slate-400">from any supplier.</span>
              </h3>
              <p className="mt-3 text-[13px] leading-relaxed text-slate-600">
                Snap or upload any invoice (on or off-network). <strong className="text-slate-900">Approved in seconds</strong> — lender pays your supplier direct, you settle on the due date.
              </p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-900 group-hover:text-lime-700 transition-colors">
                See invoice financing
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </a>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* OFF-NETWORK TOOLS — PO Generator + Invoice Financing            */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <BuyerOffNetworkSection />

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* BENTO — 8 buyer moats (reuses MoatTile + BUYER_MOAT_TILES)      */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section id="bento" className="relative py-10 md:py-14 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="buyers-bento-section">
        <Reveal>
          <Eyebrow>What you get on verqo</Eyebrow>
        </Reveal>
        <Reveal delay={0.06}>
          <h2 className="mt-5 text-3xl md:text-5xl leading-[1.02] tracking-tight font-semibold text-slate-900 max-w-3xl">
            Eight things <span className="bg-lime-300 px-2 -rotate-[0.4deg] inline-block">retailers and resellers</span><br /> have been waiting for.
          </h2>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-5 text-[15px] md:text-[17px] text-slate-600 max-w-2xl leading-relaxed">
            One portable credit line. Sixty-second approvals. Goods released the moment you click pay. Wholesale buying without the paper, the phone calls, or the wire delays.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 auto-rows-[1fr]" data-testid="buyers-bento-grid">
          {BUYER_MOAT_TILES.map((t, i) => (
            <MoatTile
              key={`buyer-moat-${i}`}
              n={i + 1}
              icon={t.icon}
              title={t.title}
              body={t.body}
              variant={t.variant}
              testid={`buyers-page-moat-tile-${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* PRICING TEASE — buyers pay $0                                   */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section id="pricing" className="relative py-10 md:py-14 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="buyers-pricing">
        <Reveal>
          <div className="rounded-3xl bg-[#0c1612] text-white px-8 md:px-14 py-14 md:py-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
              style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "32px 32px" }}
            />
            <div className="relative grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
              <div>
                <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-lime-400 font-semibold mb-5">
                  <CircleDot className="w-2.5 h-2.5 fill-lime-400 text-lime-400" /> Pricing for buyers
                </div>
                <h2 className="text-4xl md:text-6xl font-semibold tracking-tight leading-[1.02]">
                  $0 platform fee. <span className="text-white/40">Ever.</span>
                </h2>
                <p className="mt-5 text-[15px] text-slate-300 leading-relaxed max-w-md">
                  No subscription, no application fees, no underwriting fees. Suppliers cover the cost of verqo because the value they get (same-day cash, Allianz insurance, zero collections) makes it worth it for them.
                </p>
                <p className="mt-3 text-[12.5px] text-slate-400 leading-relaxed max-w-md">
                  You still cover the pass-through costs of doing business — wires, card processing, shipping, dropship and fulfillment fees. We don't mark them up.
                </p>
                <a href="/waitlist?as=buyer" data-testid="buyers-pricing-cta" className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-lime-300 text-slate-900 text-sm font-semibold hover:bg-lime-200 transition-colors">
                  Get pre-approved — free
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400 font-semibold mb-5">What's free · what's not</div>
                <ul className="grid grid-cols-1 gap-3">
                  {[
                    { ok: true,  t: "Free: account, credit application & underwriting" },
                    { ok: true,  t: "Free: credit line, terms negotiation & invoicing" },
                    { ok: true,  t: "Free: payment history portable across all suppliers" },
                    { ok: false, t: "You cover: wires, card processing & ACH fees (at cost)" },
                    { ok: false, t: "You cover: shipping, dropship & fulfillment fees" },
                  ].map((f) => (
                    <li key={f.t} className="flex items-start gap-2.5 text-[13.5px] text-slate-200 leading-relaxed">
                      {f.ok ? (
                        <CheckCircle2 className="w-4 h-4 text-lime-400 mt-0.5 flex-shrink-0" />
                      ) : (
                        <CircleDot className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      )}
                      <span>{f.t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* WHAT'S NEXT · buyer roadmap (mirrors seller page format)        */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section id="roadmap" className="relative py-10 md:py-14 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="buyers-roadmap">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <Eyebrow>What's next on verqo</Eyebrow>
            <h2 className="mt-5 text-3xl md:text-5xl leading-[1.02] tracking-tight font-semibold text-slate-900">
              Shipping next, <span className="italic text-slate-400">for buyers.</span>
            </h2>
            <p className="mt-4 text-[15px] text-slate-600 leading-relaxed max-w-2xl mx-auto">
              The capabilities we're rolling out over the coming quarters. Seller-side list is on the <Link to="/" className="underline decoration-lime-400 underline-offset-4 hover:text-slate-900" data-testid="buyers-roadmap-sellers-link">sellers page</Link>.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 max-w-3xl mx-auto">
          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 md:p-9" data-testid="buyers-roadmap-card">
              <div className="flex items-center gap-2 mb-6">
                <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold inline-flex items-center gap-2">
                  <CircleDot className="w-2.5 h-2.5 fill-lime-500 text-lime-500" />
                  For buyers
                </div>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                {BUYER_ROADMAP.map((r, i) => (
                  <li key={r.title} className="flex items-start gap-3" data-testid={`buyers-roadmap-item-${i + 1}`}>
                    <span className="mono text-[10px] text-slate-400 tracking-[0.15em] mt-1 flex-shrink-0 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <div className="text-[14px] font-semibold text-slate-900 leading-snug">{r.title}</div>
                      <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">{r.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* FAQ moved to the dedicated /faq route — accessible from the     */}
      {/* footer. Keeping the buyer landing focused on the conversion.    */}
      {/* ═══════════════════════════════════════════════════════════════ */}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* FINAL CTA                                                       */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section id="cta" className="relative py-12 md:py-16 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="buyers-final-cta">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <Eyebrow>Wholesale, on your terms</Eyebrow>
            <h2 className="mt-5 text-[36px] md:text-[60px] leading-[0.98] font-semibold tracking-tight text-slate-900">
              One line. <span className="text-slate-400">Every supplier.</span><br />
              Apply in <span className="bg-lime-300 px-3 -rotate-1 inline-block text-[1.05em]">60 seconds.</span>
            </h2>
            <p className="mt-7 text-[15px] md:text-[17px] leading-relaxed text-slate-600 max-w-xl mx-auto">
              Pre-approval is free. The platform is free. You only pay the pass-through costs of doing business — at cost.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <a href="/waitlist?as=buyer" data-testid="buyers-final-cta-primary" className="group inline-flex items-center gap-2 px-8 py-4 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-all bg-[#0c1612]">
                Get pre-approved — free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link to="/" data-testid="buyers-final-cta-secondary" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg border border-slate-300 text-slate-900 text-sm hover:bg-slate-50 transition-colors">
                For sellers →
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* TRUST · bank-grade compliance badges (mirrors sellers page)     */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section className="relative py-10 md:py-14 px-6 md:px-12 bg-slate-50/40 border-t border-slate-100" data-testid="buyers-trust">
        <div className="max-w-[1280px] mx-auto">
          <Reveal>
            <div className="text-center text-[11px] uppercase tracking-[0.28em] text-slate-500 mb-10">
              Bank-grade compliance · examiner-ready out of the box
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="buyers-trust-grid">
              {TRUST_BADGES.map((b) => (
                <div
                  key={b.label}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-5 flex flex-col items-center text-center hover:border-lime-400 hover:-translate-y-0.5 transition-all"
                  data-testid={`buyers-trust-badge-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <div className="w-9 h-9 rounded-lg grid place-items-center mb-2.5 bg-[#0c1612]">
                    <b.icon className="w-4 h-4 text-lime-300" strokeWidth={1.8} />
                  </div>
                  <div className="text-[12.5px] font-semibold text-slate-900">{b.label}</div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">{b.sub}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      {!(hideFooter || trimmedForPreview) && (
      <footer className="relative py-12 px-6 md:px-12 max-w-[1280px] mx-auto border-t border-slate-200" data-testid="buyers-footer">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-10">
          <div className="col-span-2">
            <div className="flex items-center gap-2">
              <VerqoLockup />
            </div>
            <p className="mt-3 text-[11.5px] text-slate-500 leading-relaxed max-w-xs">
              Wholesale, instant. Net-30/60/90 in seconds. One credit line across every supplier. Buyers pay $0.
            </p>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mb-3">Product</div>
            <ul className="space-y-2 text-[12.5px] text-slate-700">
              <li><Link to="/" className="hover:text-slate-900 transition-colors" data-testid="buyers-footer-link-sellers">For sellers</Link></li>
              <li><a href="#how-it-works" className="hover:text-slate-900 transition-colors" data-testid="buyers-footer-link-how">How it works</a></li>
              <li><a href="#pricing" className="hover:text-slate-900 transition-colors" data-testid="buyers-footer-link-pricing">Pricing</a></li>
              <li><Link to="/partners" className="hover:text-slate-900 transition-colors" data-testid="buyers-footer-link-partners">Partners</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mb-3">Company</div>
            <ul className="space-y-2 text-[12.5px] text-slate-700">
              <li><Link to="/about" className="hover:text-slate-900 transition-colors" data-testid="buyers-footer-link-about">About us</Link></li>
              <li><Link to="/faq" className="hover:text-slate-900 transition-colors" data-testid="buyers-footer-link-faq">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-slate-900 transition-colors" data-testid="buyers-footer-link-contact">Contact us</Link></li>
              <li><a href="/legal/terms" className="hover:text-slate-900 transition-colors" data-testid="buyers-footer-link-terms">Terms &amp; Privacy</a></li>
            </ul>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-slate-100 text-[10.5px] uppercase tracking-[0.22em] text-slate-400">
          <div>© 2026 verqo · Wholesale, instant.</div>
          <div className="normal-case tracking-normal text-[11px] text-slate-500">All rights reserved.</div>
        </div>
      </footer>
      )}
    </div>
  );
}
